// Copyright ©️ 2026 eVolve MEP, LLC

import { Button, Text } from '@mantine/core';

import type { NavOption } from 'app/Layout/NavBar/globalNavigationMenu';
import { navbarTransitionStyle } from 'app/Layout/NavBar/useAppShell';
import { EvolveIcon, type EvolveIconName } from 'assets/icons/EvolveIcon';

import classes from './NavSectionHeader.module.css';

type Props = {
  navData: {
    label: string;
    icon: EvolveIconName;
    options: NavOption[];
  };
  navbarExpanded: boolean;
} & (
  | {
      collapsible: true;
      navOptionsOpened: boolean;
      onClick: () => void;
    }
  | {
      /** A non-collapsible header renders as a plain heading, since there is nothing to toggle. */
      collapsible: false;
      navOptionsOpened?: never;
      onClick?: never;
    }
);

export const NavSectionHeader = ({ navData, navbarExpanded, collapsible, navOptionsOpened, onClick }: Props) => (
  <Button
    component={collapsible ? 'button' : 'div'}
    role={collapsible ? undefined : 'heading'}
    aria-level={collapsible ? undefined : 2}
    fullWidth
    style={{
      'transition': navbarTransitionStyle,
      'alignItems': 'center',
      '--button-hover': collapsible ? 'var(--mantine-color-app-shell-gray-7)' : 'transparent',
      'cursor': collapsible ? undefined : 'default',
    }}
    classNames={{
      section: classes.section,
    }}
    p={5}
    size="input-sm"
    justify="flex-start"
    leftSection={<EvolveIcon icon={navData.icon} color="app-shell-gray.5" />}
    rightSection={
      collapsible && (
        <EvolveIcon
          icon={navOptionsOpened ? 'NavCollapseOpen' : 'NavCollapseCollapsed'}
          color="app-shell-gray.5"
          spanProps={{
            className: classes.collapseCaret,
          }}
        />
      )
    }
    variant="transparent"
    onClick={onClick}
  >
    <Text className={classes.text} h={navbarExpanded ? undefined : 0} opacity={navbarExpanded ? 1 : 0}>
      {navData.label}
    </Text>
  </Button>
);
